import { z } from "zod";
import type { Data } from "@puckeditor/core";
import type { ServiceAddress } from "./address";

export type { Data } from "@puckeditor/core";

/** Loose validation of a Puck document at the API boundary — the forms Puck
 *  config validates field props at render; here we just guard the envelope. */
export const PuckDataSchema = z.object({
  content: z.array(z.record(z.string(), z.unknown())),
  root: z.record(z.string(), z.unknown()).optional(),
  zones: z.record(z.string(), z.unknown()).optional(),
});

/**
 * Form data = a Puck document rendered by the forms Puck config. This module is
 * the Puck-free read side: parsing a saved document into a flat field list for
 * value collection, required-gating, server-side key-filtering, and inbox
 * labeling. The field's Puck component `id` IS its key in `formData.custom`.
 */

export const FORM_FIELD_TYPES = [
  "text",
  "textarea",
  "select",
  "multiselect",
  "number",
  "date",
  "boolean",
  "image",
  "email",
  "phone",
  "address",
] as const;
export type FieldType = (typeof FORM_FIELD_TYPES)[number];

// `address` fields carry a structured ServiceAddress once a Places suggestion is
// picked (a bare string until then / on freeform entry).
export type FieldValue = string | number | boolean | string[] | ServiceAddress | undefined;

export interface FieldDef {
  /** Puck component instance id — the stable key under formData.custom. For a
   *  core field this is its canonical top-level formData key instead. */
  id: string;
  type: FieldType;
  label: string;
  required: boolean;
  helpText?: string;
  options?: { label: string; value: string }[];
  autoComplete?: string;
}

/** Forms Puck component type → field type. The one place the mapping lives. */
export const FIELD_COMPONENT_TYPE: Record<string, FieldType> = {
  TextField: "text",
  TextareaField: "textarea",
  SelectField: "select",
  MultiSelectField: "multiselect",
  NumberField: "number",
  DateField: "date",
  BooleanField: "boolean",
  ImageField: "image",
};

/**
 * Core contact fields — Puck components bound to the canonical TOP-LEVEL
 * formData keys (name/email/phone/message), never formData.custom, so contact
 * dedup, notifications, and workflows read the same keys no matter how the
 * form was built. Their `FieldDef.id` is the canonical key (the Puck instance
 * id is ignored), and only the first instance of each counts.
 */
export interface CoreFieldSpec {
  key: string;
  type: FieldType;
  autoComplete?: string;
}

export const CORE_FIELD_COMPONENTS: Record<string, CoreFieldSpec> = {
  NameField: { key: "name", type: "text", autoComplete: "name" },
  EmailField: { key: "email", type: "email", autoComplete: "email" },
  PhoneField: { key: "phone", type: "phone", autoComplete: "tel" },
  // "address" is the canonical key the FieldPulse lead mapping ultimately reads
  // (via the hoisted address columns) and the emergency preset writes — a custom
  // field can't reach either. Its
  // "address" type renders the Places autocomplete (field-input.tsx).
  AddressField: { key: "address", type: "address", autoComplete: "street-address" },
  MessageField: { key: "message", type: "textarea" },
};

export const CORE_FIELD_KEYS: ReadonlySet<string> = new Set(
  Object.values(CORE_FIELD_COMPONENTS).map((c) => c.key),
);

export const EMPTY_FORM_DATA: Data = { content: [], root: {} } as unknown as Data;

/** Flatten a saved Puck document into field defs (top-level content only). */
export function fieldsFromData(data: Data | null | undefined): FieldDef[] {
  const content = (data as { content?: unknown[] } | null | undefined)?.content;
  if (!Array.isArray(content)) return [];
  const out: FieldDef[] = [];
  const seenCoreKeys = new Set<string>();
  for (const item of content) {
    const node = item as { type?: string; props?: Record<string, unknown> };
    const props = node.props ?? {};
    const core = node.type ? CORE_FIELD_COMPONENTS[node.type] : undefined;
    if (core) {
      // Only the first instance of a core field binds its canonical key — a
      // hand-crafted doc can't double-bind (or shadow) name/email/phone/message.
      if (seenCoreKeys.has(core.key)) {
        continue;
      }
      seenCoreKeys.add(core.key);
      out.push({
        id: core.key,
        type: core.type,
        label: typeof props.label === "string" ? props.label : "",
        required: props.required === true || props.required === "yes",
        helpText: typeof props.helpText === "string" ? props.helpText : undefined,
        autoComplete: core.autoComplete,
      });
      continue;
    }
    const type = node.type ? FIELD_COMPONENT_TYPE[node.type] : undefined;
    if (!type) continue;
    const id = typeof props.id === "string" ? props.id : "";
    if (!id) continue;
    // Core keys are reserved: a hand-crafted custom field with id "email" etc.
    // must not collide with (or impersonate) a core field's canonical key.
    if (CORE_FIELD_KEYS.has(id)) {
      continue;
    }
    out.push({
      id,
      type,
      label: typeof props.label === "string" ? props.label : "",
      required: props.required === true || props.required === "yes",
      helpText: typeof props.helpText === "string" ? props.helpText : undefined,
      options: Array.isArray(props.options)
        ? (props.options as { label: string; value: string }[])
        : undefined,
    });
  }
  return out;
}

/** Canonical keys of the core fields present in a form doc. Keyed off the
 *  component TYPE (not the flattened id), so a custom field crafted with a
 *  core id can't impersonate a core field. */
export function coreFieldKeys(data: Data | null | undefined): Set<string> {
  const content = (data as { content?: unknown[] } | null | undefined)?.content;
  const keys = new Set<string>();
  if (!Array.isArray(content)) {
    return keys;
  }
  for (const item of content) {
    const type = (item as { type?: string }).type;
    const core = type ? CORE_FIELD_COMPONENTS[type] : undefined;
    if (core) {
      keys.add(core.key);
    }
  }
  return keys;
}

/** A contact form must keep a way to reach the lead: an Email or Phone field.
 *  The shared guardrail — the builder disables Publish on it and the API
 *  rejects the PUT (CONTACT_CHANNEL_REQUIRED). */
export function hasContactChannel(data: Data | null | undefined): boolean {
  const keys = coreFieldKeys(data);
  return keys.has("email") || keys.has("phone");
}

/** Split collected field values into the canonical top-level submit keys (core
 *  fields) and everything else (→ the payload's `customFields`). */
export function splitCoreValues(values: Record<string, FieldValue>): {
  core: Record<string, FieldValue>;
  custom: Record<string, FieldValue>;
} {
  const core: Record<string, FieldValue> = {};
  const custom: Record<string, FieldValue> = {};
  for (const [key, value] of Object.entries(values)) {
    if (value === undefined) {
      continue;
    }
    if (CORE_FIELD_KEYS.has(key)) {
      core[key] = value;
    } else {
      custom[key] = value;
    }
  }
  return { core, custom };
}

/**
 * Preset-parity default for the contact form: what the builder starts from
 * when no row exists yet (mirrors the ContactForm "contact" preset — name and
 * email required, phone and message optional). Live surfaces do NOT render
 * this — an absent row means "keep the preset markup" (§8); parity lives here
 * in code, not in migrated rows. See wiki/decisions/form-builder-contact-tasks.md.
 */
export const DEFAULT_CONTACT_FORM_DATA: Data = {
  content: [
    { type: "NameField", props: { id: "NameField-core", label: "Your Name", required: "yes" } },
    { type: "EmailField", props: { id: "EmailField-core", label: "Email Address", required: "yes" } },
    { type: "PhoneField", props: { id: "PhoneField-core", label: "Phone Number", required: "no" } },
    { type: "MessageField", props: { id: "MessageField-core", label: "Your Message", required: "no" } },
  ],
  root: {},
} as unknown as Data;

function isEmpty(v: FieldValue): boolean {
  return v === undefined || v === "" || v === false || (Array.isArray(v) && v.length === 0);
}

/* ── Form-level ("Page") settings, stored in the Puck doc's root.props ── */

function rootProps(data: Data | null | undefined): Record<string, unknown> {
  const root = (data as { root?: { props?: Record<string, unknown> } } | null | undefined)?.root;
  return root?.props ?? {};
}

export interface NoteConfig {
  enabled: boolean;
  label: string;
  required: boolean;
}

/** The "Anything else?" free-text note config. Off by default — the note shows
 *  only when a form explicitly enables it (noteEnabled "yes"). */
export function noteConfigFromData(data: Data | null | undefined): NoteConfig {
  const p = rootProps(data);
  return {
    enabled: p.noteEnabled === "yes" || p.noteEnabled === true,
    label:
      typeof p.noteLabel === "string" && p.noteLabel.trim()
        ? p.noteLabel.trim()
        : "Anything we should know?",
    required: p.noteRequired === "yes" || p.noteRequired === true,
  };
}

/** Configured "Additional details" bullets (booking page). Empty when unset →
 *  the surface falls back to its built-in defaults. Reads the legacy `goodToKnow`
 *  key for booking forms persisted before the rename. */
export function additionalDetailsFromData(data: Data | null | undefined): string[] {
  const p = rootProps(data);
  const items = p.additionalDetails ?? p.goodToKnow;
  if (!Array.isArray(items)) return [];
  return items
    .map((i) => (typeof (i as { text?: unknown })?.text === "string" ? (i as { text: string }).text.trim() : ""))
    .filter(Boolean);
}

/** Heading over the "Additional details" bullets (root.props.additionalDetailsHeading).
 *  Falls back to "Additional details" when unset; reads the legacy
 *  `goodToKnowHeading` key for forms persisted before the rename. */
export function additionalDetailsHeadingFromData(data: Data | null | undefined): string {
  const p = rootProps(data);
  const h = p.additionalDetailsHeading ?? p.goodToKnowHeading;
  return typeof h === "string" && h.trim() ? h.trim() : "Additional details";
}

/** Configured booking-page hero image (root.props.heroImageUrl). Empty when
 *  unset → the surface falls back to the GBP profile photo, then the monogram. */
export function heroImageFromData(data: Data | null | undefined): string {
  const url = rootProps(data).heroImageUrl;
  return typeof url === "string" ? url.trim() : "";
}

/** Per-step header overrides (title/subtitle), stored under root.props.stepText
 *  keyed by booking step. Empty when unset → each step falls back to its default
 *  wording. Read by the booking widget (live + the form-builder canvas). */
export function stepTextFromData(
  data: Data | null | undefined,
): Record<string, { title?: string; subtitle?: string }> {
  const t = rootProps(data).stepText;
  if (!t || typeof t !== "object" || Array.isArray(t)) {
    return {};
  }
  return t as Record<string, { title?: string; subtitle?: string }>;
}

/** Ids of required fields that have no value yet — drives Continue gating. */
export function missingRequiredFromData(
  data: Data | null | undefined,
  values: Record<string, FieldValue>,
): string[] {
  return fieldsFromData(data)
    .filter((f) => f.required && isEmpty(values[f.id]))
    .map((f) => f.id);
}
